import React, { type ComponentPropsWithoutRef, forwardRef, type ReactNode } from 'react';
import { Blue500, Green600, ICON_COLOR, Mint700, Pink400, Purple500, Yellow700 } from '@zep-us/zus/tokens';
import { BoltFillIcon, OrderIcon, TextLongLeftIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { CheckSmallCircleFillIcon, MessageLinesFillIcon, PlusSmallIcon, SquareGridSemiFillIcon } from '@/icons';
import { WithPlanBadge } from '@/shared/components/Badge/PlanBadge';
import { LocalizedOxTypeIcon } from '@/shared/components/LocalizedOxTypeIcon';
import { useUpsellingGuard, useUserPlanManager } from '@/shared/plan';
import { Button, type ButtonProps } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { type QuizType } from './type';
import { useQuizList } from './useQuizList';
type BuildQuizButtonsLayoutMode = 'page' | 'ai-shell';
export const BuildQuizButtons = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'> & {
  layoutMode?: BuildQuizButtonsLayoutMode;
}>((props, ref) => {
  const {
    className,
    layoutMode = 'page',
    ...rest
  } = props;
  const {
    t
  } = useTranslation(['common', 'build']);
  const {
    handleCreate
  } = useQuizList();
  const upsellingGuard = useUpsellingGuard();
  const {
    userPlanManager
  } = useUserPlanManager();
  const featurePlanType = userPlanManager.getFeaturePlanType('MATCH');
  const orderFeaturePlanType = userPlanManager.getFeaturePlanType('ORDER');
  const handleButtonClick = (type: QuizType) => {
    logger.track('add_quizItem', {
      type,
      position: 'button'
    });
    handleCreate(type);
  };
  return <div {...rest} className={cn('flex w-full flex-col gap-xs', layoutMode === 'ai-shell' ? 'min-[1025px]:grid min-[1025px]:grid-cols-3 min-[1025px]:gap-sm' : 'tablet-sm:grid tablet-sm:grid-cols-3 tablet-sm:gap-sm', className)} ref={ref}>
      <BuildQuizItemButton onMouseDown={e => {
      e.stopPropagation();
      e.preventDefault();
    }} onClick={() => handleButtonClick('multiple')} text={t('build:build.multiple.button')} icon={<CheckSmallCircleFillIcon fill={Green600} />} />
      <BuildQuizItemButton onMouseDown={e => {
      e.stopPropagation();
      e.preventDefault();
    }} onClick={() => handleButtonClick('subjective')} text={t('build:build.subjective.button')} icon={<MessageLinesFillIcon fill={Blue500} />} />
      <BuildQuizItemButton onMouseDown={e => {
      e.stopPropagation();
      e.preventDefault();
    }} onClick={() => handleButtonClick('ox')} text={t('build:build.ox.button')} icon={<LocalizedOxTypeIcon size="md" fill={Purple500} />} />
      <BuildQuizItemButton onMouseDown={e => {
      e.stopPropagation();
      e.preventDefault();
    }} onClick={() => handleButtonClick('essay')} text={t('build:build.quizType.essayQuestion')} icon={<TextLongLeftIcon size="md" fill={Pink400} />} />
      <div className="w-full [&>div]:w-full">
        <WithPlanBadge isBadgeVisible={true} wrapperClassName="h-[48px] w-full rounded-[8px] border border-opacity-assistive bg-default shadow-[0px_1px_1px_0px_rgba(28,27,31,0.12),0px_0px_3px_0px_rgba(28,27,31,0.04)]" planType={featurePlanType} className="pointer-events-none right-[-5px] top-[-6px] size-[18px] translate-x-0 translate-y-0" glyph={<BoltFillIcon width={12} height={12} fill={ICON_COLOR.inverse} aria-hidden />}>
          <BuildQuizItemButton onMouseDown={e => {
          e.stopPropagation();
          e.preventDefault();
        }} onClick={() => upsellingGuard(() => handleButtonClick('match'), {
          feature: 'MATCH',
          from: 'match',
          initialPlanType: 'PRO'
        })} text={t('common:quizCard.typeBadge.match')} icon={<SquareGridSemiFillIcon size="md" fill={Yellow700} />} className="h-full shadow-none" variant="outlined" />
        </WithPlanBadge>
      </div>
      <div className="w-full [&>div]:w-full">
        <WithPlanBadge isBadgeVisible={true} wrapperClassName="h-[48px] w-full rounded-sm border border-opacity-assistive bg-default shadow-xs" planType={orderFeaturePlanType} className="pointer-events-none right-[-5px] top-[-6px] size-[18px] translate-x-0 translate-y-0" glyph={<BoltFillIcon width={12} height={12} fill={ICON_COLOR.inverse} aria-hidden />}>
          <BuildQuizItemButton onMouseDown={e => {
          e.stopPropagation();
          e.preventDefault();
        }} onClick={() => upsellingGuard(() => handleButtonClick('order'), {
          feature: 'ORDER',
          from: 'order',
          initialPlanType: 'PRO'
        })} text={t('common:quizCard.typeBadge.order')} icon={<OrderIcon size="md" fill={Mint700} />} className="h-full shadow-none" variant="outlined" />
        </WithPlanBadge>
      </div>
    </div>;
});
BuildQuizButtons.displayName = 'BuildQuizButtons';
type BuildQuizItemButtonProps = ButtonProps & {
  text: string;
  icon: ReactNode;
};
const BuildQuizItemButton = forwardRef<HTMLButtonElement, BuildQuizItemButtonProps>((props, ref) => {
  const {
    text,
    icon,
    onClick,
    className,
    variant = 'contained',
    ...rest
  } = props;
  return <Button {...rest} className={cn('flex w-full justify-center border-opacity-assistive shadow-xs focus:focus-ring', className, variant === 'contained' && 'bg-default')} size={'lg'} variant={variant} intent={'neutral'} onClick={onClick} ref={ref}>
      {icon}
      <span>{text}</span>
      <PlusSmallIcon from={ICON_COLOR.default} />
    </Button>;
});
BuildQuizItemButton.displayName = 'BuildQuizItemButton';